import './AddTrayDetails.scss'
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Button,
  Paper,
  OutlinedInput,
} from '@mui/material'

import { LoadingButton } from 'components/mui'
import useTrayConfiguration from 'views/TrayConfiguration/TrayConfiguration.logic'
import useAddTrayDetailsLogic from './AddTrayDetails.logic'
import { useUser } from 'app/User'
import { useAddTrayContext } from 'views/TrayManagementLayout/AddTray/AddTray.context'
import { TrayType } from 'components/organisms/AddTraySelectType/AddTraySelectType.types'
import { useTrayStepContext } from 'views/TrayManagementLayout/AddTray/AddTray.logic'
import { DTM_MANUFACTURERS } from 'lib/dtm-manufacturers'
import { useEffect, useState, useMemo } from 'react'

const AddTrayDetails = () => {
  const { user, isRep } = useUser()
  const { nextStep, prevStep } = useTrayStepContext()
  const showRepOptions =
    isRep || !user?.roles?.length || user?.roles?.includes('MGAT_GUEST')

  const {
    trayDescription,
    handleTrayDescriptionChange,
    selectedLocationId,
    handleSelectedLocationChange,
    trayVendor,
    handleTrayVendorChange,
    trayWeight,
    handleTrayWeightChange,
    renderDropOffDateTime,
    getTrayCategoryOptions,
    selectedTraySetName,
    setSelectedTraySetName,
  } = useAddTrayDetailsLogic()

  const {
    selectedTrayCategory,
    setSelectedTrayCategory,
    hasSubTrays,
    setHasSubTrays,
  } = useAddTrayContext()

  const { locationsWithParents, findLocation, isLoadingLocations } =
    useTrayConfiguration()

  const [apiManufacturers, setApiManufacturers] = useState<
    { value: string; label: string }[]
  >([])
  const [isLoadingManufacturers, setIsLoadingManufacturers] = useState(true)
  const [trayTemplates, setTrayTemplates] = useState<Record<
    string,
    any
  > | null>(null)

  const handleNext = () => {
    if (
      trayDescription &&
      selectedLocationId &&
      trayVendor &&
      selectedTrayCategory &&
      selectedTraySetName
    ) {
      nextStep()
    }
  }

  useEffect(() => {
    const fetchManufacturers = async () => {
      setIsLoadingManufacturers(true)
      try {
        const baseUrl = process.env.REACT_APP_DTM_COMPUTER_VISION_API
        if (!baseUrl) {
          throw new Error(
            'REACT_APP_DTM_COMPUTER_VISION_API environment variable is not set.'
          )
        }

        const apiUrl = `${baseUrl.replace(/\/$/, '')}/api/tray-templates`
        const response = await fetch(apiUrl)

        if (!response.ok) {
          throw new Error(
            `Failed to fetch manufacturer data: ${response.status}`
          )
        }

        const data = await response.json()

        if (data && typeof data === 'object') {
          const manufacturers: Array<{ value: string; label: string }> =
            Object.keys(data).map((key) => ({
              value: key.toLowerCase(),
              label: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
            }))

          setApiManufacturers(manufacturers)
          // store templates with lower-cased vendor keys so look-ups match the dropdown value
          const normalizedTemplates: Record<string, any> = {}
          Object.entries(data).forEach(([k, v]) => {
            normalizedTemplates[k.toLowerCase()] = v
          })
          setTrayTemplates(normalizedTemplates)
        }
      } catch (error) {
        console.error('Error fetching manufacturers:', error)
        setApiManufacturers([...DTM_MANUFACTURERS])
      } finally {
        setIsLoadingManufacturers(false)
      }
    }

    fetchManufacturers()
  }, [])

  const manufacturerOptions: Array<{ value: string; label: string }> =
    apiManufacturers.length > 0 ? apiManufacturers : [...DTM_MANUFACTURERS]

  const uniqueManufacturerOptions = manufacturerOptions.filter(
    (option, index, self) =>
      index === self.findIndex((o) => o.value === option.value)
  )

  const traySetOptions = useMemo(() => {
    if (!trayVendor || !trayTemplates) return []

    const vendorKey = trayVendor.toLowerCase()
    const vendorTemplates = trayTemplates[vendorKey] // ← array
    if (!Array.isArray(vendorTemplates)) return []

    const uniqueNames = new Set<string>()
    vendorTemplates.forEach((tpl: any) => {
      const raw =
        tpl.tray_set_name || tpl.traySetName || tpl.set_name || tpl.name
      if (raw) uniqueNames.add(raw)
    })

    return Array.from(uniqueNames).map((name) => ({
      value: name,
      label: name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    }))
  }, [trayVendor, trayTemplates])

  return (
    <>
      <Paper className="add-tray-details-step step-item">
        <div className="add-tray-details-step_content">
          {renderDropOffDateTime()}

          <FormControl fullWidth>
            <Select
              fullWidth
              variant="outlined"
              displayEmpty
              renderValue={(selected) =>
                !selected ? (
                  <Typography variant="body1" color="gray">
                    Select Tray Storage Location (required)
                  </Typography>
                ) : (
                  findLocation(selected as number)?.name
                )
              }
              value={selectedLocationId || ''}
              onChange={handleSelectedLocationChange}
            >
              <MenuItem disabled value="">
                {isLoadingLocations
                  ? 'Loading Locations...'
                  : 'Select Tray Storage Location'}
                Select Tray Storage Location
              </MenuItem>
              {locationsWithParents.map((location) => (
                <MenuItem
                  data-testid="hardware-menuitem"
                  style={{ cursor: 'pointer', fontSize: 16 }}
                  key={location.id}
                  value={location.id}
                >
                  {location.name},{' '}
                  {findLocation(location.parentLocationId as number)?.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              You can create new locations from the location management
              dashboard
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth>
            <Select
              data-testid="add-tray-location-storage_input"
              fullWidth
              variant="outlined"
              displayEmpty
              renderValue={(selected) =>
                !selected ? (
                  <Typography variant="body1" color="gray">
                    Select Tray {!showRepOptions ? 'Manufacturer' : 'Vendor'}{' '}
                    (required)
                  </Typography>
                ) : (
                  uniqueManufacturerOptions.find(
                    (mfr: { value: string; label: string }) =>
                      mfr.value === selected
                  )?.label
                )
              }
              value={trayVendor || ''}
              onChange={handleTrayVendorChange}
              input={<OutlinedInput />}
            >
              <MenuItem disabled value="">
                Select Tray {!showRepOptions ? 'Manufacturer' : 'Vendor'}
              </MenuItem>
              {uniqueManufacturerOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  style={{ cursor: 'pointer', fontSize: 16 }}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {trayVendor && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <Select
                fullWidth
                variant="outlined"
                displayEmpty
                value={selectedTraySetName || ''}
                onChange={(e) =>
                  setSelectedTraySetName(e.target.value as string)
                }
                renderValue={(sel) =>
                  !sel ? (
                    <Typography color="gray">
                      Select Tray-Set Name&nbsp;(required)
                    </Typography>
                  ) : (
                    traySetOptions.find((o) => o.value === sel)?.label
                  )
                }
              >
                <MenuItem disabled value="">
                  Select Tray-Set Name
                </MenuItem>
                {traySetOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            autoComplete="off"
            placeholder="Enter Tray Details/ Description. This information will be shown to RN in the OR, and is required for appropriate documentation and invoicing."
            fullWidth
            multiline
            minRows={4}
            maxRows={7}
            type="text"
            value={trayDescription}
            onChange={handleTrayDescriptionChange}
            variant="outlined"
          />

          <TextField
            autoComplete="off"
            placeholder="Tray Weight lbs (optional)"
            fullWidth
            type="number"
            value={trayWeight || ''}
            onChange={handleTrayWeightChange}
            variant="outlined"
          />

          <div className="tray-category_selection">
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={selectedTrayCategory || 'facility'}
                onChange={(e) =>
                  setSelectedTrayCategory(
                    e.target.value as TrayType['category']
                  )
                }
              >
                {getTrayCategoryOptions().map((trayCategory) => (
                  <FormControlLabel
                    key={trayCategory}
                    value={trayCategory}
                    control={<Radio />}
                    label={`${trayCategory
                      .charAt(0)
                      .toUpperCase()}${trayCategory.slice(1)}`}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </div>

          <FormControl fullWidth>
            <Typography variant="subtitle1" gutterBottom>
              Tray Structure
            </Typography>
            <RadioGroup
              row
              value={hasSubTrays}
              onChange={(e) => setHasSubTrays(e.target.value === 'true')}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="Multi-Level Tray or Any Tray With Sub-Tray(s)"
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="Single Level Tray"
              />
            </RadioGroup>
            <FormHelperText>
              Select whether this is a single level tray or a multi level tray
              or any tray with subtray(s)
            </FormHelperText>
          </FormControl>
        </div>
      </Paper>
      <div className="buttons_container">
        <Button variant="outlined" onClick={prevStep}>
          Back
        </Button>
        <LoadingButton
          dataTestId="add-tray-submit_button"
          disabled={
            !trayDescription ||
            !selectedLocationId ||
            !trayVendor ||
            !selectedTrayCategory ||
            !selectedTraySetName
          }
          onClick={handleNext}
        >
          Next
        </LoadingButton>
      </div>
    </>
  )
}

export default AddTrayDetails
